import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import { usePopup } from "../common/Popup";
import visibilityIcon from "../../assets/visibility.png";
import attachWhiteIcon from "../../assets/attach whit.png";
import "../features/users/user-table.css";

const DEFAULT_GRID_COLUMNS = "1.35fr 0.9fr 1fr 1fr 0.75fr 0.75fr 0.85fr 0.65fr";

const ContentArea = styled.div`
  padding: 0px 0 20px;
`;

const TableWrapper = styled.div`
  border-right: 1px solid #dfe3e8;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 18px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  align-items: center;
  padding-left: 20px;
  height: 50px;
  background: #f2f0f0;
  font-size: 16px;
  font-weight: 700;
  color: #6ab8a8;

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;

    &:last-child {
      border-right: none;
    }
  }
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  align-items: center;
  padding-left: 20px;
  height: 58px;
  font-size: 16px;
  color: #555555;
  font-weight: 500;
  border-top: 1px solid #e6e6e6;
  font-family: 'Public Sans', sans-serif;

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;

    &:last-child {
      border-right: none;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;

  .btn-download,
  .btn-approve,
  .btn-reject {
    justify-content: center;
    margin-left: 0;
    gap: 8px;
  }

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const StatusButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  color: #ffffff;
  background: ${({ $variant }) => {
    if ($variant === "danger") return "#da1515";
    if ($variant === "success") return "#6AB8A8";
    return "#ff9f1f";
  }};
  cursor: pointer;
  font-size: 14px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 4px 12px;
  color: #ffffff;
  background: ${({ $sent }) => ($sent ? "#6AB8A8" : "#da1515")};
  font-size: 14px;
`;

const ActionMenuWrapper = styled.div`
  position: relative;
`;

const ActionMenuButton = styled.button`
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  background: #ff9f1f;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const ActionMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 10;
  min-width: 190px;
  padding: 5px;
  border: 1px solid #dddddd;
  border-radius: 7px;
  background: #ffffff;
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.16);
`;

const ActionMenuItem = styled.button`
  width: 100%;
  border: none;
  border-radius: 5px;
  padding: 9px 10px;
  background: transparent;
  color: #555555;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #fff1df;
    color: #d97800;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const EmptyMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: #555555;
  font-size: 15px;
`;

const TermoCompromissoContent = ({ searchTerm = "", pendingOnly = false, user }) => {
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingAlunoId, setUploadingAlunoId] = useState(null);
  const [openActionAlunoId, setOpenActionAlunoId] = useState(null);
  const fileInputRefs = useRef({});
  const actionMenuRef = useRef(null);
  const navigate = useNavigate();
  const popup = usePopup();
  const isEstabelecimentoSaude = Number(user?.cargoId) === 4;
  const isInstituicaoEnsino = [2, 3].includes(Number(user?.cargoId));
  const showEstabelecimentoActions = isEstabelecimentoSaude && !pendingOnly;
  const gridColumns = isEstabelecimentoSaude
    ? showEstabelecimentoActions
      ? "1.35fr 0.9fr 1fr 1fr 0.75fr 0.85fr 0.65fr"
      : "1.35fr 0.9fr 1fr 1fr 0.75fr 0.65fr"
    : DEFAULT_GRID_COLUMNS;

  const fetchAlunos = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getTermosCompromissoAlunos();
      const data = result?.data ? result.data : result;
      setAlunos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Falha ao carregar alunos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlunos();
  }, []);

  useEffect(() => {
    const closeActionMenu = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setOpenActionAlunoId(null);
      }
    };

    document.addEventListener("mousedown", closeActionMenu);
    return () => document.removeEventListener("mousedown", closeActionMenu);
  }, []);

  const filteredAlunos = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const statusFiltered = pendingOnly
      ? alunos.filter((aluno) => !aluno.termoRecebido)
      : isEstabelecimentoSaude
        ? alunos.filter((aluno) => aluno.termoRecebido)
        : alunos;

    if (!normalizedSearch) return statusFiltered;

    return statusFiltered.filter((aluno) => {
      const values = [
        aluno.aluno,
        aluno.matricula,
        aluno.tipo,
        aluno.setor,
        aluno.turno,
      ];

      return values.some((value) =>
        String(value || "").toLowerCase().includes(normalizedSearch)
      );
    });
  }, [alunos, isEstabelecimentoSaude, pendingOnly, searchTerm]);

  const handleDownload = async (aluno) => {
    if (!aluno.termoRecebido) {
      await apiService.gerarTermoCompromissoAluno(
        aluno.id,
        `Termo de Compromisso - ${aluno.aluno}.docx`
      );
      return;
    }

    await apiService.downloadTermoCompromissoAluno(
      aluno.id,
      aluno.termoCompromissoNome || `termo-${aluno.aluno}.pdf`
    );
  };

  const handleDownloadOriginal = async (aluno) => {
    await apiService.gerarTermoCompromissoAluno(
      aluno.id,
      `Termo de Compromisso - ${aluno.aluno}.docx`
    );
  };

  const handleUploadClick = async (aluno) => {
    await popup.alert({
      icon: "info",
      title: "Nome do arquivo",
      message:
        "Recomendamos nomear o PDF assinado com o padrão: termo_nome-do-aluno_matricula.pdf. Use apenas PDF.",
      confirmText: "Selecionar arquivo",
    });

    fileInputRefs.current[aluno.id]?.click();
  };

  const handleUpload = async (alunoId, file) => {
    if (!file) return;
    setUploadingAlunoId(alunoId);
    try {
      await apiService.uploadTermoCompromissoAluno(alunoId, file);
      await fetchAlunos();
    } catch (err) {
      // Falha de envio não é falha da lista: `error` esconde a tabela inteira,
      // e o usuário ficava sem como tentar de novo sem sair da tela.
      await popup.alert({
        icon: "error",
        title: "Erro ao enviar termo",
        message: err.message || "Falha ao enviar termo.",
      });
    } finally {
      setUploadingAlunoId(null);
    }
  };

  const handleVer = (aluno) => {
    const returnPath = pendingOnly
      ? "/TermoCompromissoPendentes"
      : "/TermoCompromisso";

    if (isEstabelecimentoSaude) {
      navigate(`/Confirmadas/${aluno.solicitacaoId}`, {
        state: { returnPath },
      });
      return;
    }

    navigate("/CadastrarAlunos", {
      state: {
        solicitacaoId: aluno.solicitacaoId,
        vagaId: aluno.vagaId,
        returnPath,
      },
    });
  };

  return (
    <ContentArea>
      {loading && <EmptyMessage>Carregando...</EmptyMessage>}
      {error && <EmptyMessage>{error}</EmptyMessage>}

      {!loading && !error && alunos.length === 0 && (
        <EmptyMessage>Nenhum aluno cadastrado para termo de compromisso.</EmptyMessage>
      )}

      {!loading && !error && alunos.length > 0 && pendingOnly && filteredAlunos.length === 0 && !searchTerm && (
        <EmptyMessage>Nenhum aluno pendente de envio do termo assinado.</EmptyMessage>
      )}

      {!loading && !error && alunos.length > 0 && isEstabelecimentoSaude && !pendingOnly && filteredAlunos.length === 0 && !searchTerm && (
        <EmptyMessage>Nenhum termo assinado recebido.</EmptyMessage>
      )}

      {!loading && !error && alunos.length > 0 && filteredAlunos.length === 0 && searchTerm && (
        <EmptyMessage>Nenhum aluno encontrado para a busca.</EmptyMessage>
      )}

      {!loading && !error && filteredAlunos.length > 0 && (
        <TableWrapper>
          <HeaderRow $columns={gridColumns}>
            <div>ALUNO</div>
            <div>MATRÍCULA</div>
            <div>TIPO</div>
            <div>SETOR</div>
            <div>TURNO</div>
            {isInstituicaoEnsino ? (
              <>
                <div>TERMO DE COMPROMISSO</div>
                <div>AÇÃO</div>
              </>
            ) : (
              <>
                {(!isEstabelecimentoSaude || showEstabelecimentoActions) && <div>BAIXAR</div>}
                {!isEstabelecimentoSaude && <div>ENVIAR</div>}
              </>
            )}
            <div>VER</div>
          </HeaderRow>

          {filteredAlunos.map((aluno) => (
            <ListRow key={aluno.id} $columns={gridColumns}>
              <div>{aluno.aluno || "-"}</div>
              <div>{aluno.matricula || "-"}</div>
              <div>{aluno.tipo || "-"}</div>
              <div>{aluno.setor || "-"}</div>
              <div>{aluno.turno || "-"}</div>
              {isInstituicaoEnsino ? (
                <>
                  <ActionsCell>
                    <StatusBadge $sent={aluno.termoRecebido}>
                      {aluno.termoRecebido ? "Enviado ✓" : "Pendente ⚠"}
                    </StatusBadge>
                  </ActionsCell>
                  <ActionsCell>
                    <HiddenFileInput
                      type="file"
                      accept="application/pdf"
                      ref={(element) => {
                        fileInputRefs.current[aluno.id] = element;
                      }}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        handleUpload(aluno.id, file);
                        event.target.value = "";
                      }}
                    />
                    <ActionMenuWrapper
                      ref={openActionAlunoId === aluno.id ? actionMenuRef : null}
                    >
                      <ActionMenuButton
                        type="button"
                        onClick={() =>
                          setOpenActionAlunoId((current) =>
                            current === aluno.id ? null : aluno.id
                          )
                        }
                        aria-expanded={openActionAlunoId === aluno.id}
                      >
                        Baixar/Enviar
                      </ActionMenuButton>
                      {openActionAlunoId === aluno.id && (
                        <ActionMenu>
                          <ActionMenuItem
                            type="button"
                            onClick={() => {
                              setOpenActionAlunoId(null);
                              handleDownloadOriginal(aluno);
                            }}
                          >
                            Baixar termo original
                          </ActionMenuItem>
                          <ActionMenuItem
                            type="button"
                            disabled={uploadingAlunoId === aluno.id}
                            onClick={() => {
                              setOpenActionAlunoId(null);
                              handleUploadClick(aluno);
                            }}
                          >
                            Enviar termo assinado
                          </ActionMenuItem>
                          {aluno.termoRecebido && (
                            <ActionMenuItem
                              type="button"
                              onClick={() => {
                                setOpenActionAlunoId(null);
                                handleDownload(aluno);
                              }}
                            >
                              Baixar termo assinado
                            </ActionMenuItem>
                          )}
                        </ActionMenu>
                      )}
                    </ActionMenuWrapper>
                  </ActionsCell>
                </>
              ) : (
                <>
              {(!isEstabelecimentoSaude || showEstabelecimentoActions) && (
                <ActionsCell>
                  <button type="button" className="btn-download" onClick={() => handleDownload(aluno)}>
                    Baixar
                    <img src={attachWhiteIcon} alt="" />
                  </button>
                </ActionsCell>
              )}
              {!isEstabelecimentoSaude && <ActionsCell>
                <HiddenFileInput
                  type="file"
                  accept="application/pdf"
                  ref={(element) => {
                    fileInputRefs.current[aluno.id] = element;
                  }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    handleUpload(aluno.id, file);
                    event.target.value = "";
                  }}
                />
                <StatusButton
                  type="button"
                  $variant={aluno.termoRecebido ? "success" : "danger"}
                  disabled={uploadingAlunoId === aluno.id}
                  onClick={() => handleUploadClick(aluno)}
                >
                  {aluno.termoRecebido ? "Enviado" : "Enviar"}
                  <img src={attachWhiteIcon} alt="" />
                </StatusButton>
              </ActionsCell>}
                </>
              )}
              <ActionsCell>
                <button
                  type="button"
                  className="btn-view-icon"
                  onClick={() => handleVer(aluno)}
                  title="Ver aluno"
                >
                  <img
                    src={visibilityIcon}
                    alt="Ver"
                    style={{ width: 22, height: 22 }}
                  />
                </button>
              </ActionsCell>
            </ListRow>
          ))}
        </TableWrapper>
      )}
    </ContentArea>
  );
};

export default TermoCompromissoContent;
